import storage from '../storage';
import axios from 'axios';

// This file is prepared for API integration in the future
// Current implementation uses localStorage, but includes API functions for future use

// API Base URL for backend integration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Storage key for fallback localStorage implementation
// const STORAGE_KEY = 'requiredDocuments';

// Helper function to get token for API requests
const getToken = async () => {
  const token = await storage.getToken();
  return token;
};

// Get company information from localStorage
const getCompany = async () => {
  try {
    const userData = await storage.getUser();
    if (userData) {
      const user = JSON.parse(userData);
      return user.company;
    }
    return null;
  } catch (error) {
    console.error('Error getting company info:', error);
    return null;
  }
};

// -------------------------
// API Implementation for backend integration
// -------------------------

// Get all required documents
const getAllRequiredDocuments = async (companyOverride = null) => {
  try {
    // Get user data and token
    const userDataString = await storage.getUser();
    const userData = userDataString ? JSON.parse(userDataString) : null;
    const token = await storage.getToken();
    
    if (!userData || !token) {
      throw new Error('User data or authentication token not found');
    }
    
    // Use provided company or fall back to user's company
    const targetCompany = companyOverride || userData.company;
    
    if (!targetCompany) {
      throw new Error('Company information not available');
    }
    
    // API request to get required documents for this company
    const response = await axios.post(`${API_BASE_URL}/required-documents/list`, {
      company: targetCompany
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    if (response.status === 200) {
      // Ensure data is an array
      const documents = response.data.success ? response.data.data : [];
      return {
        ok: true,
        data: documents
      };
    }
    
    return {
      ok: false,
      error: 'Failed to fetch required documents',
      data: []
    };
  } catch (error) {
    console.error('Error fetching required documents:', error);
    return {
      ok: false,
      error: error.message || 'Failed to fetch required documents',
      data: []
    };
  }
};

// Get a single required document by ID
const getRequiredDocumentById = async (id) => {
  try {
    const token = await getToken();
    
    const response = await axios.get(
      `${API_BASE_URL}/required-documents/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return { 
      ok: response.data.success, 
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('API Error fetching document:', error);
    // Fallback to localStorage if API fails
    // return getRequiredDocumentByIdLocal(id);
  }
};

// Create a new required document
const createRequiredDocument = async (documentData) => {
  try {
    const token = await getToken();
    
    // Use company from documentData if provided, otherwise get from storage
    let company = documentData.company;
    if (!company) {
      company = await getCompany();
    }
    
    if (!company) {
      return { ok: false, error: 'Company information not found' };
    }
    
    // Ensure company is included in document data
    const docWithCompany = {
      ...documentData,
      company
    };
    
    const response = await axios.post(
      `${API_BASE_URL}/required-documents`,
      docWithCompany,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return { 
      ok: response.data.success, 
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('API Error creating document:', error);
    return {
      ok: false,
      error: error.response?.data?.message || error.message || 'Failed to create document'
    };
  }
};

// Update an existing required document
const updateRequiredDocument = async (id, documentData) => {
  try {
    const token = await getToken();
    
    // Ensure company information is included if not already present
    let docDataWithCompany = { ...documentData };
    if (!docDataWithCompany.company) {
      const company = await getCompany();
      if (company) {
        docDataWithCompany.company = company;
      }
    }
    
    const response = await axios.put(
      `${API_BASE_URL}/required-documents/${id}`,
      docDataWithCompany,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return { 
      ok: response.data.success, 
      data: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('API Error updating document:', error);
    return {
      ok: false,
      error: error.response?.data?.message || error.message || 'Failed to update document'
    };
  }
};

// Delete a required document
const deleteRequiredDocument = async (id) => {
  try {
    const token = await getToken();
    
    const response = await axios.delete(
      `${API_BASE_URL}/required-documents/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    return { 
      ok: response.data.success,
      message: response.data.message
    };
  } catch (error) {
    console.error('API Error deleting document:', error);
    return {
      ok: false,
      error: error.response?.data?.message || error.message || 'Failed to delete document'
    };
  }
};

// Document Review API Functions
const get_employee_documents = (employeeId) => 
  axios.get(`${API_BASE_URL}/employee-documents/list`, {
   params: {
     employeeId: employeeId
   }
 });

// Bulk Employee Documents API Function (optimized for admin views)
const get_bulk_employee_documents = async (employeeIds, company) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/employee-documents/bulk`, {
      employeeIds,
      company
    });
    return response;
  } catch (error) {
    console.error('Error fetching bulk employee documents:', error);
    throw error;
  }
};

const approve_document = (data) => 
 axios.post(`${API_BASE_URL}/admin/employee-documents/approve`, data);

const reject_document = (data) => 
 axios.post(`${API_BASE_URL}/admin/employee-documents/reject`, data);

// Export all functions
const _expObject = {
  getAllRequiredDocuments,
  getRequiredDocumentById,
  createRequiredDocument,
  updateRequiredDocument,
  deleteRequiredDocument,
  get_employee_documents,
  get_bulk_employee_documents,
  approve_document,
  reject_document
};

export default _expObject; 